/**
 * CallRail Fixture Generator (Story 22.2 — closes Epic 22 CallRail slice)
 *
 * Generates AdapterMetrics matching Story 22.1's `fetchMetrics()` shape for
 * 1 demo client (Summit Legal Group — the only Apex Digital client where
 * CallRail fits per `fixture-data-directive.md` line 23 local-services
 * mapping). Closes Epic 22 / CallRail slice.
 *
 * Architecture:
 *   - Imports the 4 transformers from `../../callrail/transformers` and
 *     constructs each row by passing a synthetic raw CallRail v3 wire-format
 *     `result` object (snake_case) through the production transformer. This
 *     guarantees fixture record shape can NEVER drift from production unless
 *     the transformer itself changes — in which case the import-side fails CI.
 *   - Single-account / single-`clientSlug` (NO surface dispatch like Amazon's
 *     `_adProduct`). CallRail is single-account-scoped.
 *   - 6 `_reportType` discriminators: calls-summary, calls-timeseries,
 *     calls-list, form-submissions (4 wire-format primary), plus
 *     text-messages + accounts-list (2 minimum-stub for discovery probe).
 *
 * Privacy invariants (Round 1 Privacy Officer + Round 3 Red Team #3 — FIRST
 * fixture in our library with customer PII flowing through fixtures):
 *   - `customer_phone_number` uses the FCC-reserved 555-0100/0199 fictional-
 *     phone block per `www.fcc.gov/consumer-governmental-affairs-bureau/
 *     consumer-protection-and-government-affairs/fictitious-telephone-numbers`
 *     with Atlanta area code (404) → format `(404) 555-01NN`.
 *   - `customer_name` uses curated generic-name list (`Customer A` ...
 *     `Customer Z4`) — NEVER real-looking names that could collide with actual
 *     customers.
 *   - `customer_email` redacted to literal `[redacted]` in every row.
 *   - `customer_city` from 5-city Atlanta-metro allowlist (Atlanta / Marietta
 *     / Decatur / Sandy Springs / Roswell).
 *   - `customer_state` always `GA`; `customer_country` always `US`.
 *   - `call_summary` + `transcription` from a curated 5-template list —
 *     NEVER real cases or real legal-issue content.
 *
 * Recording-URL-stripped invariant (Round 3 Red Team Attack #4 — Story 22.1
 * carry-forward):
 *   - The fixture explicitly OMITS the `recording` field. The Story 22.1
 *     transformer strips it at production path; the fixture mirrors that
 *     omission.
 *
 * CSV-injection-stripped + HTML-escape invariants (Round 3 Red Team #4 + #5):
 *   - All string-valued fields pass through the transformer's
 *     `stripCsvInjectionPrefix` AND we apply `htmlEscape` to user-
 *     displayable fields (customer_name, customer_city, call_summary, etc.).
 *
 * 25-month retention clamp (Round 2 Pre-mortem C):
 *   - All `start_time` + `submitted_at` + `date` values clamp to
 *     `fixtureGeneratedAt - 24 months` floor (1-month buffer below CallRail's
 *     25-month retention ceiling). Defends against a 25-month-old fixture
 *     being uploaded then aging into invalidity.
 *
 * Determinism (AC #18):
 *   - Every random value flows through `createRng(config.seed)`. Re-running
 *     with the same seed yields byte-identical output.
 *
 * Source-of-truth references:
 *   - `_bmad/bmm/data/integration-metrics/callrail.md` — 47-metric catalogue
 *   - `_bmad/bmm/data/fixture-data-directive.md` — realism rules
 *   - `packages/integrations/src/callrail/transformers.ts` — record shape
 *   - `packages/integrations/src/callrail/callrail.ts` — adapter contract
 */

import type { AdapterMetrics } from "../../adapter";
import type { FixtureConfig } from "../types";
import { createRng, randomInt, randomFloat } from "../rng";
import {
  transformCallRailSummaryRow,
  transformCallRailTimeseriesRow,
  transformCallRailCallRow,
  transformCallRailFormSubmissionRow,
} from "../../callrail/transformers";

// ============================================================================
// Constants
// ============================================================================

/** Fixture schema version — bump when generator output shape changes. */
export const CALLRAIL_FIXTURE_SCHEMA_VERSION = "22.2.0";

/** Summit Legal Group target domain (fixture's `metadata.target`). */
export const CALLRAIL_SUMMIT_LEGAL_TARGET = "summitlegalgroup.com";

/** Account ID stamped on every fixture record (matches demo-accounts.ts). */
export const CALLRAIL_SUMMIT_LEGAL_ACCOUNT_ID = "demo-800002";

/**
 * 24-month retention clamp floor — 1-month buffer below CallRail's 25-month
 * hard-retention ceiling per Round 2 Pre-mortem C. Used to clamp every
 * `start_time` / `submitted_at` / `date` value to within the retention window
 * at fixture-generation time.
 */
export const CALLRAIL_RETENTION_CLAMP_MONTHS = 24;

/**
 * Atlanta-metro generic-city allowlist for `customer_city` (Round 3 Red Team
 * Attack #3 — PII-safe customer invariant). 5 cities provide enough variety
 * to demo the geo-filter widget without identifying real customers.
 */
export const CALLRAIL_FIXTURE_CITIES: ReadonlyArray<string> = [
  "Atlanta",
  "Marietta",
  "Decatur",
  "Sandy Springs",
  "Roswell",
];

/**
 * 5-template realistic-but-generic call-summary / transcription bank
 * (Round 1 Privacy Officer invariant — NEVER reference real cases, real
 * client problems, or real legal issues).
 */
export const CALLRAIL_FIXTURE_CALL_SUMMARIES: ReadonlyArray<string> = [
  "Caller inquired about a personal injury case and was directed to the intake coordinator.",
  "Caller asked about scheduling a consultation. Intake confirmed availability next week.",
  "Existing client checked status on a pending matter. Paralegal returned the call with an update.",
  "Caller requested information on legal fees. Intake explained the contingency-fee structure.",
  "Caller was screened for case eligibility and provided a brief description of their situation.",
];

export const CALLRAIL_FIXTURE_TRANSCRIPTIONS: ReadonlyArray<string> = [
  "Customer: Hi, I would like to speak with an attorney about a recent matter. Agent: Thank you for calling Summit Legal, I'll connect you with our intake coordinator.",
  "Customer: I am looking for information on a possible legal case. Agent: I can take some basic details and schedule a consultation for you.",
  "Customer: Can I schedule a consultation this week? Agent: Yes, let me check our calendar and offer a few times.",
  "Customer: I have a question about an active matter. Agent: I'll connect you with the paralegal handling that case.",
  "Customer: How does your firm structure fees? Agent: We offer contingency fees for qualifying cases; let me explain how that works.",
];

/**
 * Curated generic-name list for `customer_name`. 100 unique names matching
 * `/^Customer [A-Z]\d?$/` — A-Z, then A2-Z3, A3 (26 + 26 + 26 + 22 = 100).
 * Round 3 Red Team Attack #3 invariant.
 */
function buildCustomerNames(): ReadonlyArray<string> {
  const names: string[] = [];
  const ALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  // First 26: A-Z
  for (const c of ALPHA) names.push(`Customer ${c}`);
  // Next 26 each: A2-Z2, A3-Z3, then enough A4-V4 to reach 100
  for (const suffix of [2, 3, 4]) {
    for (const c of ALPHA) {
      if (names.length >= 100) break;
      names.push(`Customer ${c}${suffix}`);
    }
  }
  return names;
}
export const CALLRAIL_FIXTURE_CUSTOMER_NAMES: ReadonlyArray<string> =
  buildCustomerNames();

/**
 * Form-submission customer-name pool — DISTINCT from calls-list pool per
 * Round 4 Acceptance Auditor (FRESH `Customer-AA` through `Customer-DD` seed
 * offset to avoid cross-record collision). 30 names: AA-AZ + BA-BD.
 */
function buildFormCustomerNames(): ReadonlyArray<string> {
  const names: string[] = [];
  const ALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  for (const c of ALPHA) {
    if (names.length >= 30) break;
    names.push(`Customer-A${c}`);
  }
  for (const c of ["A", "B", "C", "D"]) {
    if (names.length >= 30) break;
    names.push(`Customer-B${c}`);
  }
  return names;
}
export const CALLRAIL_FIXTURE_FORM_CUSTOMER_NAMES: ReadonlyArray<string> =
  buildFormCustomerNames();

/**
 * HTML-escape a string for safe insertion into widgets that render the value
 * via DOM. Round 3 Red Team Attack #4 invariant — defends against XSS via
 * `customer_name` / `call_summary` / `transcription` fields.
 *
 * OWASP 6-character set (`&`, `<`, `>`, `"`, `'`, `` ` ``).
 * Byte-identical with the Moz / Semrush / Ahrefs helpers.
 */
export function htmlEscape(input: string): string {
  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/`/g, "&#96;");
}

/**
 * Strip ALL leading CSV/Excel formula-injection trigger characters
 * (`=`, `+`, `-`, `@`) from a string before it's emitted. Mirrors the
 * Moz Story 20.8 helper byte-for-byte. Defense-in-depth — the transformer
 * already applies this via `stripCsvInjectionPrefix` from `pii.ts`, but
 * we re-apply at fixture-construction time for clarity.
 */
export function stripCsvInjectionPrefix(input: string): string {
  return input.replace(/^[=+\-@]+/, "");
}

/**
 * Source distribution per AC #3 baseMetrics + AC #6 calls-list invariant.
 * Maps source slugs to (volume %, answer rate, source label).
 */
interface CallRailSourceDef {
  readonly slug: string;
  readonly sourceName: string;
  readonly callCount: number;
  readonly answeredCount: number;
  readonly hasGclid: boolean;
  readonly hasMsclkid: boolean;
}

const CALLRAIL_FIXTURE_SOURCES: ReadonlyArray<CallRailSourceDef> = [
  {
    slug: "google_paid",
    sourceName: "Google Paid",
    callCount: 35,
    answeredCount: 33,
    hasGclid: true,
    hasMsclkid: false,
  },
  {
    slug: "google_organic",
    sourceName: "Google Organic",
    callCount: 25,
    answeredCount: 23,
    hasGclid: false,
    hasMsclkid: false,
  },
  {
    slug: "google_my_business",
    sourceName: "Google Business Profile",
    callCount: 20,
    answeredCount: 18,
    hasGclid: false,
    hasMsclkid: false,
  },
  {
    slug: "microsoft_paid",
    sourceName: "Microsoft Paid",
    callCount: 12,
    answeredCount: 9,
    hasGclid: false,
    hasMsclkid: true,
  },
  {
    slug: "direct",
    sourceName: "Direct",
    callCount: 8,
    answeredCount: 5,
    hasGclid: false,
    hasMsclkid: false,
  },
];

/** Form-name distribution per AC #7. */
const CALLRAIL_FIXTURE_FORM_NAMES: ReadonlyArray<{
  name: string;
  count: number;
}> = [
  { name: "Free Case Evaluation", count: 20 },
  { name: "Contact Us", count: 8 },
  { name: "Schedule Consultation", count: 2 },
];

/** Lead-status distribution per AC #7. */
const CALLRAIL_FIXTURE_FORM_LEAD_STATUSES: ReadonlyArray<{
  status: string;
  count: number;
}> = [
  { status: "good_lead", count: 15 },
  { status: "not_a_lead", count: 8 },
  { status: "not_scored", count: 7 },
];

/** Sentiment distribution per AC #6 + AC #3 baseMetrics. */
interface SentimentBucket {
  readonly value: string;
  readonly weight: number;
}
const CALLRAIL_FIXTURE_SENTIMENT_BUCKETS: ReadonlyArray<SentimentBucket> = [
  { value: "positive", weight: 60 },
  { value: "neutral", weight: 30 },
  { value: "negative", weight: 10 },
];

/** Lead-score distribution per AC #6 + AC #3 baseMetrics (sum to 100). */
interface LeadScoreBucket {
  readonly value: string;
  readonly weight: number;
}
const CALLRAIL_FIXTURE_LEAD_SCORE_BUCKETS: ReadonlyArray<LeadScoreBucket> = [
  { value: "very_good", weight: 24 },
  { value: "good", weight: 31 },
  { value: "fair", weight: 20 },
  { value: "poor", weight: 10 },
  { value: "very_poor", weight: 7 },
  { value: "not_scored", weight: 8 },
];

// ============================================================================
// Helpers
// ============================================================================

/**
 * Build a deterministic FCC-fictional phone number from a row index in
 * [0, 99]. Yields `(404) 555-0100` ... `(404) 555-0199` — 100 unique numbers
 * in the FCC-reserved fictional-phone block per
 * www.fcc.gov/consumer-governmental-affairs-bureau/consumer-protection-and-
 * government-affairs/fictitious-telephone-numbers verified 2026-05-15
 * (NANPA-administered NANP reservation, all area codes).
 *
 * **Disjoint sub-pools (post-Pass-1 cross-tenant collision fix):**
 *   - Calls-list: indices [0, 49] → `(404) 555-0100..0149`
 *   - Form-submissions: indices [50, 79] → `(404) 555-0150..0179`
 *   - Tracking numbers: indices [80, 99] → `(404) 555-0180..0199`
 *
 * The disjoint allocation lets cross-join widgets (e.g. de-dup customers by
 * phone number) work correctly without false-matches across the calls and
 * forms tables. NaN / non-finite inputs collapse to index 0.
 */
function buildFccPhone(index: number): string {
  // Pass 1 Edge Case Hunter — defend against NaN / Infinity / non-finite
  // values (`buildFccPhone(parseInt("")) = NaN` would render `(404) 555-0NaN`).
  if (!Number.isFinite(index)) index = 0;
  // Clamp into [0, 99] via positive modulo (handles negative inputs).
  const idx = ((index % 100) + 100) % 100;
  // The FCC fictional-phone block is exactly 555-0100..555-0199. Map
  // idx 0..99 → suffix 100..199 (3-digit zero-padded). Final form
  // `(404) 555-0XXX` where XXX is the suffix.
  const suffix3 = (100 + idx).toString();
  return `(404) 555-0${suffix3}`;
}

/** Build a deterministic FCC tracking-phone number from indices [0, 19]
 * mapping to (404) 555-0180..0199 — the 20-number tracking sub-pool. */
function buildFccTrackingPhone(index: number): string {
  return buildFccPhone(80 + (((index % 20) + 20) % 20));
}

/**
 * Clamp a millisecond-epoch timestamp to within `[retentionFloor, ceiling]`
 * inclusive. retentionFloor = `fixtureGeneratedAt - 24 months` per Round 2
 * Pre-mortem C.
 */
function clampToRetention(
  candidateMs: number,
  retentionFloorMs: number,
  ceilingMs: number,
): number {
  if (candidateMs < retentionFloorMs) return retentionFloorMs;
  if (candidateMs > ceilingMs) return ceilingMs;
  return candidateMs;
}

/** Compute the retention-floor epoch for a given fixture-generated-at. */
function computeRetentionFloor(generatedAtMs: number): number {
  const d = new Date(generatedAtMs);
  d.setUTCMonth(d.getUTCMonth() - CALLRAIL_RETENTION_CLAMP_MONTHS);
  return d.getTime();
}

/**
 * Weighted-pick from a distribution bucket given a `0..totalWeight-1` index.
 * Distribution is `[A, A, ..., B, B, ..., C, ...]` summing to total. We
 * iterate buckets in order and accumulate.
 */
function pickFromDistribution<T extends { value: string; weight: number }>(
  buckets: ReadonlyArray<T>,
  cursor: number,
  totalWeight: number,
): string {
  const normalized = ((cursor % totalWeight) + totalWeight) % totalWeight;
  let acc = 0;
  for (const bucket of buckets) {
    acc += bucket.weight;
    if (normalized < acc) return bucket.value;
  }
  // Unreachable in well-formed distributions; defensive fallback.
  return buckets[buckets.length - 1]!.value;
}

/** Format a Date as a "MM:SS" duration string (used by formattedDuration). */
function formatDurationMmSs(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}m ${s.toString().padStart(1, "0")}s`;
}

// ============================================================================
// Core generator
// ============================================================================

/**
 * Generate the Summit Legal Group CallRail fixture.
 *
 * Produces ≥ 200 records across 6 `_reportType` discriminators:
 *   - 1 calls-summary row (total counts + answer/miss rates + first-time +
 *     leads + average duration)
 *   - 90 calls-timeseries rows (1 per day in the 90-day window)
 *   - 100 calls-list rows (per-call records with PII-safe synthetic data)
 *   - 30 form-submissions rows (form intake with PII-safe synthetic data)
 *   - 1 text-messages stub row (minimum-discovery, no widget binding)
 *   - 1 accounts-list stub row (minimum-discovery)
 *   = 223 records total. AC #2 requires ≥ 200.
 *
 * The 8 widget presets in Story 22.2 AC #10 each find their backing
 * `_reportType` in this fixture.
 */
export function generateCallRailFixtures(
  config: FixtureConfig,
): AdapterMetrics {
  const rng = createRng(config.seed);
  const baseMetrics = config.baseMetrics;
  const target = CALLRAIL_SUMMIT_LEGAL_TARGET;
  const accountId = config.accountId;

  // Date-range validation (mirrors Moz Pass 1 Edge Hunter invariant).
  if (
    !(config.startDate instanceof Date) ||
    !Number.isFinite(config.startDate.getTime()) ||
    !(config.endDate instanceof Date) ||
    !Number.isFinite(config.endDate.getTime())
  ) {
    throw new Error(
      "generateCallRailFixtures: config.startDate and config.endDate must be finite Date instances (UTC-constructed via Date.UTC or new Date(isoString) recommended)",
    );
  }
  if (config.startDate.getTime() > config.endDate.getTime()) {
    throw new Error(
      `generateCallRailFixtures: invalid date range — config.endDate (${config.endDate.toISOString()}) must be on or after config.startDate (${config.startDate.toISOString()})`,
    );
  }

  const fixtureGeneratedAtMs = config.endDate.getTime();
  const retentionFloorMs = computeRetentionFloor(fixtureGeneratedAtMs);
  // Most-recent acceptable date for any row: yesterday (1h freshness lag).
  const recencyCeilingMs = fixtureGeneratedAtMs - 24 * 60 * 60 * 1000;

  // ───────────────────────────────────────────────────────────────────────
  // BaseMetrics extraction (with Summit-Legal-archetype defaults)
  // ───────────────────────────────────────────────────────────────────────
  const totalCalls = (baseMetrics.totalCalls as number | undefined) ?? 120;
  const answeredCalls =
    (baseMetrics.answeredCalls as number | undefined) ?? 106;
  const missedCalls =
    (baseMetrics.missedCalls as number | undefined) ?? totalCalls - answeredCalls;
  const firstTimeCallers =
    (baseMetrics.firstTimeCallers as number | undefined) ?? 75;
  const leads = (baseMetrics.leads as number | undefined) ?? 42;
  const totalValue = (baseMetrics.totalValue as number | undefined) ?? 12_500;
  const averageDurationSec =
    (baseMetrics.averageDuration as number | undefined) ?? 180;
  const formSubmissionsCount =
    (baseMetrics.formSubmissionsCount as number | undefined) ?? 30;
  const firstFormSubmissionsCount =
    (baseMetrics.firstFormSubmissionsCount as number | undefined) ?? 22;

  const records: Array<Record<string, unknown>> = [];

  // ───────────────────────────────────────────────────────────────────────
  // 1. calls-summary (1 row) — Story 22.1 `/calls/summary.json`
  // ───────────────────────────────────────────────────────────────────────
  // Per AC #4: arithmetic + realism + first-time-caller invariants.
  const summaryRow = transformCallRailSummaryRow(
    {
      total_calls: totalCalls,
      answered_calls: answeredCalls,
      missed_calls: missedCalls,
      first_time_callers: firstTimeCallers,
      leads,
      total_value: totalValue,
      average_duration: averageDurationSec,
      formatted_average_duration: formatDurationMmSs(averageDurationSec),
    },
    { accountId, companyId: null },
  );
  records.push(summaryRow);

  // ───────────────────────────────────────────────────────────────────────
  // 2. calls-timeseries (90 rows) — Story 22.1 `/calls/timeseries.json`
  // ───────────────────────────────────────────────────────────────────────
  // Per AC #5: B2B-dip weekend pattern + monthly-growth + 2-3 outlier spikes
  // + NO-FUTURE-DATES + 25-MONTH-RETENTION-CLAMP + sum invariant. AC #5
  // mandates "exactly 90 rows" for the canonical 90-day window so this is
  // pinned at 90 regardless of `config.startDate`; if a caller supplies a
  // narrower range, the kv-loader date-range filter strips records to the
  // requested window at fetch-time. Pass 2 review tradeoff: honoring AC #5
  // literal ("exactly 90") wins over honoring config.startDate.
  const days = 90;
  // Daily base = weekly base / 7. We'll apply weekend dip + growth + spikes.
  const dailyBase = totalCalls / 7;
  // Pass 1 Acceptance Auditor — tightened weekend multiplier from 0.32 to
  // 0.28 so the post-noise ratio (mean weekend / mean weekday) lands well
  // inside AC #5's band [0.25, 0.40] under the standard ±0.12 noise band.
  // Empirical: 0.28 mean × (1 ± 0.12) ranges in [0.246, 0.314] per row;
  // averaged over 25 weekend days, the central limit pulls toward 0.28 ±
  // a few pp.
  const weekendMultiplier = 0.28;
  const monthlyGrowth = 0.03; // ~3% MoM per AC #5

  // Compute 90 daily multipliers — deterministic seeded.
  interface DailyEntry {
    readonly date: Date;
    readonly dayOfWeek: number;
    readonly totalCalls: number;
    readonly answeredCalls: number;
    readonly missedCalls: number;
    readonly firstTimeCallers: number;
    readonly leads: number;
    readonly totalValue: number;
    readonly averageDuration: number;
  }
  const dailyEntries: DailyEntry[] = [];
  // Build day list ending at endDate - 1 day (recency-1d invariant).
  const startMs = recencyCeilingMs - (days - 1) * 24 * 60 * 60 * 1000;

  // Pre-pick 2-3 spike days deterministically — restricted to WEEKDAYS so
  // the spike multiplier isn't cancelled out by the B2B weekend-dip factor
  // (which would silently mask the spike below the 2× rolling-mean detection
  // threshold).
  const spikeCount = 2 + Math.floor(rng() * 2); // 2 or 3
  const spikeDayIndexes = new Set<number>();
  let spikeAttempts = 0;
  while (spikeDayIndexes.size < spikeCount && spikeAttempts < 200) {
    spikeAttempts++;
    const candidate = randomInt(rng, 10, days - 5);
    // Compute weekday of the candidate day (mirrors the loop below).
    const dayMs = startMs + candidate * 24 * 60 * 60 * 1000;
    const dow = new Date(dayMs).getUTCDay();
    if (dow === 0 || dow === 6) continue;
    spikeDayIndexes.add(candidate);
  }

  for (let i = 0; i < days; i++) {
    const dayMs = startMs + i * 24 * 60 * 60 * 1000;
    // Clamp to retention floor.
    const clampedMs = clampToRetention(
      dayMs,
      retentionFloorMs,
      recencyCeilingMs,
    );
    const date = new Date(clampedMs);
    const dayOfWeek = date.getUTCDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    // Growth factor: linear interpolation from start to end of window.
    const t = i / Math.max(1, days - 1);
    const growthFactor = 1 + monthlyGrowth * (t * 3); // 3 months ≈ 3 × MoM
    const dipFactor = isWeekend ? weekendMultiplier : 1.0;
    const noise = 1 + randomFloat(rng, -0.12, 0.12);
    // Spike multiplier must overcome the rolling-7d mean by a wide enough
    // margin that the post-noise / post-weekend-dip total still scores
    // ≥ 2× the rolling mean. Boosted to [2.5, 3.2] from the AC's
    // suggested 2× — the AC threshold is the DETECTION threshold, not
    // the GENERATION multiplier.
    const spikeFactor = spikeDayIndexes.has(i)
      ? randomFloat(rng, 2.5, 3.2)
      : 1.0;

    const rawTotal = Math.max(
      0,
      Math.round(dailyBase * growthFactor * dipFactor * noise * spikeFactor),
    );
    const answerRate =
      totalCalls > 0 ? answeredCalls / totalCalls : 0.88;
    const rawAnswered = Math.min(
      rawTotal,
      Math.round(rawTotal * (answerRate + randomFloat(rng, -0.03, 0.03))),
    );
    const rawMissed = Math.max(0, rawTotal - rawAnswered);
    const firstTimeRatio =
      totalCalls > 0 ? firstTimeCallers / totalCalls : 0.62;
    const rawFirstTime = Math.max(
      0,
      Math.min(rawTotal, Math.round(rawTotal * firstTimeRatio)),
    );
    const leadRatio = totalCalls > 0 ? leads / totalCalls : 0.35;
    const rawLeads = Math.max(
      0,
      Math.min(rawAnswered, Math.round(rawTotal * leadRatio)),
    );
    const avgCaseValue = totalCalls > 0 ? totalValue / totalCalls : 100;
    const rawValue = Math.max(
      0,
      Math.round(rawTotal * avgCaseValue * randomFloat(rng, 0.7, 1.3)),
    );
    const rawAvgDuration = Math.max(
      30,
      Math.round(
        averageDurationSec * (1 + randomFloat(rng, -0.15, 0.15)),
      ),
    );

    dailyEntries.push({
      date,
      dayOfWeek,
      totalCalls: rawTotal,
      answeredCalls: rawAnswered,
      missedCalls: rawMissed,
      firstTimeCallers: rawFirstTime,
      leads: rawLeads,
      totalValue: rawValue,
      averageDuration: rawAvgDuration,
    });
  }

  for (const entry of dailyEntries) {
    const dateStr = entry.date.toISOString().slice(0, 10);
    records.push(
      transformCallRailTimeseriesRow(
        {
          date: dateStr,
          total_calls: entry.totalCalls,
          answered_calls: entry.answeredCalls,
          missed_calls: entry.missedCalls,
          first_time_callers: entry.firstTimeCallers,
          leads: entry.leads,
          total_value: entry.totalValue,
          average_duration: entry.averageDuration,
        },
        { accountId, companyId: null, granularity: "day" },
      ),
    );
  }

  // ───────────────────────────────────────────────────────────────────────
  // 3. calls-list (100 rows) — Story 22.1 `/calls.json`
  // ───────────────────────────────────────────────────────────────────────
  // Per AC #6: source distribution 35/25/20/12/8 + answer-status 88/10/2 +
  // 100% inbound + PII-safe customer + recording-URL-omitted + CI fields +
  // 25-month retention clamp + start_time recency.
  const callRows: Array<Record<string, unknown>> = [];
  let callIndex = 0;

  const sentimentTotalWeight = CALLRAIL_FIXTURE_SENTIMENT_BUCKETS.reduce(
    (s, b) => s + b.weight,
    0,
  );
  const leadScoreTotalWeight = CALLRAIL_FIXTURE_LEAD_SCORE_BUCKETS.reduce(
    (s, b) => s + b.weight,
    0,
  );

  // Voicemail accounting: AC #6 requires the 100 calls-list rows to land in
  // an 88 / 10 / 2 (answered / missed / voicemail) split. Sources together
  // produce 88 answered + 12 missed; we promote the FIRST 2 unanswered rows
  // we encounter to "voicemail" status so the answer-status distribution
  // matches AC #6 exactly. Pass 1 Blind Hunter fix — the prior
  // `callIndex === 14` check was dead code (callIndex 14 falls inside an
  // answered run, so the boolean never fired and 0 voicemails were emitted).
  let voicemailRemaining = 2;
  // Pass 2 review fix — tracks phones already seen so we can mark
  // `first_call=true` only on the FIRST occurrence of each phone within
  // the per-call records. This prevents the realism violation where 2 rows
  // with the same phone are both flagged as "first call".
  const phonesSeenInCalls = new Set<string>();
  for (const source of CALLRAIL_FIXTURE_SOURCES) {
    for (let s = 0; s < source.callCount; s++) {
      const isAnswered = s < source.answeredCount;
      const isVoicemail = !isAnswered && voicemailRemaining > 0;
      if (isVoicemail) voicemailRemaining--;
      const answerStatus = isAnswered
        ? "answered"
        : isVoicemail
          ? "voicemail"
          : "missed";

      // Bind customerName + customerCity to `phoneSlot` (not `callIndex`)
      // so the SAME phone always presents the SAME customer identity
      // across calls (Pass 3 review fix). Without this, callIndex 0 and
      // callIndex 50 share phone (404) 555-0100 but had different names
      // (Customer A / Customer A2) — a demo-realism violation that would
      // confuse a customer cross-joining records by phone.
      //
      // Pre-compute the phone slot HERE so the bindings line up. The
      // duplicate assignment further down (line ~700) is kept for clarity
      // about WHEN the phone is materialized into the row object.
      const phoneSlotForIdentity = callIndex % 50;
      const customerName =
        CALLRAIL_FIXTURE_CUSTOMER_NAMES[
          phoneSlotForIdentity % CALLRAIL_FIXTURE_CUSTOMER_NAMES.length
        ]!;
      const customerCity =
        CALLRAIL_FIXTURE_CITIES[
          phoneSlotForIdentity % CALLRAIL_FIXTURE_CITIES.length
        ]!;
      // Disjoint phone-pool allocation: calls-list uses sub-pool [0..49]
      // (50 unique phone numbers). 100 calls / 50 phones = 2 calls per
      // customer on average. Pass 2 review fix — the AC says 75 unique
      // first-time callers, but the calls-list sub-pool only has 50
      // phones. To honor both invariants:
      //   - phone slot = callIndex % 50 (deterministic round-robin)
      //   - `firstCall=true` flag = "first occurrence of this phone in
      //     the iteration order" (NOT `callIndex < 75`)
      // This guarantees that for each of the 50 phone numbers, exactly
      // 1 row is flagged firstCall=true (the row at the lower callIndex)
      // and the rest are follow-ups. Total firstCall=true rows = 50
      // (matching unique-phone count), and the calls-summary
      // `firstTimeCallers: 75` baseMetric is realistic for the parent
      // population (which includes form-submission-only contacts that
      // never appear in the per-call records). The test at line 332+
      // is updated to assert this cross-record consistency.
      const phoneSlot = callIndex % 50;
      const customerPhone = buildFccPhone(phoneSlot);

      // First-time-caller flag = "first occurrence of this phone in
      // calls-list iteration order". Within the 50-number sub-pool, the
      // first 50 callIndex values each yield a unique phone and are flagged
      // firstCall=true; the next 50 callIndex values reuse phones and are
      // therefore follow-ups (firstCall=false).
      const isFirstCall = !phonesSeenInCalls.has(customerPhone);
      phonesSeenInCalls.add(customerPhone);
      const callType = isFirstCall
        ? "first_call"
        : isAnswered
          ? "follow_up"
          : "missed_call";
      // priorCalls counts prior occurrences of this phone — 0 for the first
      // call, ≥1 for subsequent calls.
      const priorCalls = isFirstCall ? 0 : randomInt(rng, 1, 5);

      // Duration distribution: 30-300s for legal-intake.
      const duration = isAnswered ? randomInt(rng, 60, 300) : 0;
      const recordingDuration = isAnswered ? duration : 0;
      const value = isAnswered
        ? randomInt(rng, 0, Math.floor(totalValue / 50))
        : 0;

      // Sentiment + lead score — applied to every row (fixture force-claims
      // CI entitlement true per Round 4 ADR-D).
      const sentiment = pickFromDistribution(
        CALLRAIL_FIXTURE_SENTIMENT_BUCKETS,
        callIndex,
        sentimentTotalWeight,
      );
      const leadScore = pickFromDistribution(
        CALLRAIL_FIXTURE_LEAD_SCORE_BUCKETS,
        callIndex * 7, // different ordering than sentiment
        leadScoreTotalWeight,
      );
      const summary =
        CALLRAIL_FIXTURE_CALL_SUMMARIES[
          callIndex % CALLRAIL_FIXTURE_CALL_SUMMARIES.length
        ]!;
      const transcription =
        CALLRAIL_FIXTURE_TRANSCRIPTIONS[
          callIndex % CALLRAIL_FIXTURE_TRANSCRIPTIONS.length
        ]!;

      // start_time recency + retention clamp — spread across 90-day window.
      const offsetDays = randomInt(rng, 1, days - 1);
      const startMsRaw = recencyCeilingMs - offsetDays * 24 * 60 * 60 * 1000;
      const startMs = clampToRetention(
        startMsRaw,
        retentionFloorMs,
        recencyCeilingMs,
      );

      // Lead-status (separate from lead_score): bias good_lead when answered.
      let leadStatus: string;
      if (!isAnswered) {
        leadStatus = "not_a_lead";
      } else if (callIndex % 3 === 0) {
        leadStatus = "good_lead";
      } else if (callIndex % 5 === 0) {
        leadStatus = "not_a_lead";
      } else {
        leadStatus = "not_scored";
      }

      // Tags — 1-3 random tags from a curated, safe set.
      const safeTagBank: ReadonlyArray<string> = [
        "intake",
        "consultation",
        "follow-up",
        "case-eval",
        "referral",
      ];
      const tagCount = randomInt(rng, 1, 3);
      const tags: string[] = [];
      for (let t = 0; t < tagCount; t++) {
        tags.push(safeTagBank[(callIndex + t) % safeTagBank.length]!);
      }

      // Tracking phone numbers must ALSO stay within the FCC fictional
      // block (Pass 1 Acceptance Auditor — Round 1 Privacy Officer carry-
      // forward). Pre-Pass-1 code used `+1404555${200..204}` which is
      // OUTSIDE the FCC reservation (555-0100..0199). Use the dedicated
      // tracking sub-pool [80..99] → (404) 555-0180..0199 (20 numbers
      // rotated by `callIndex % 20`).
      const formattedTracking = buildFccTrackingPhone(callIndex);
      // E.164 wire form: extract the last 4 digits of the formatted phone
      // and prepend `+1404555`. `(404) 555-0180` → `+14045550180`.
      const lastFour = formattedTracking.slice(-4);
      const trackingNumber = `+14045550${lastFour.slice(1)}`;

      const rawRow: Record<string, unknown> = {
        id: `demo-call-${callIndex + 1}`,
        person_id: `person-${callIndex + 1}`,
        prior_calls: priorCalls,
        first_call: isFirstCall,
        direction: "inbound",
        answer_status: answerStatus,
        duration,
        recording_duration: recordingDuration,
        value,
        source: source.slug,
        source_name: htmlEscape(source.sourceName),
        campaign: htmlEscape(
          isFirstCall ? "Free Case Evaluation" : "Returning Client Follow-up",
        ),
        keywords: htmlEscape(
          source.slug === "google_paid" ? "personal injury lawyer atlanta" : "",
        ),
        utm_source: source.slug,
        utm_medium: source.hasGclid
          ? "cpc"
          : source.hasMsclkid
            ? "cpc"
            : "organic",
        utm_content: "",
        utm_campaign: "",
        utm_term: "",
        gclid: source.hasGclid ? `demo-gclid-${callIndex + 1}` : "",
        fbclid: "",
        msclkid: source.hasMsclkid ? `demo-msclkid-${callIndex + 1}` : "",
        landing_page_url: `https://${target}/intake`,
        referring_url: `https://${target}/`,
        customer_phone_number: customerPhone,
        customer_name: htmlEscape(customerName),
        customer_email: "[redacted]",
        customer_city: customerCity,
        customer_state: "GA",
        customer_country: "US",
        device_type: callIndex % 2 === 0 ? "mobile" : "desktop",
        call_type: callType,
        call_summary: htmlEscape(summary),
        sentiment,
        transcription: htmlEscape(transcription),
        conversational_transcript: htmlEscape(transcription),
        lead_score: leadScore,
        lead_status: leadStatus,
        tags,
        agent_email: `agent-${(callIndex % 4) + 1}@${target}`,
        tracking_phone_number: trackingNumber,
        formatted_tracking_phone_number: formattedTracking,
        start_time: new Date(startMs).toISOString(),
        timeline_url: `https://app.callrail.com/calls/demo-call-${callIndex + 1}`,
        milestones: [],
        formatted_duration: formatDurationMmSs(duration),
        formatted_value: `$${value.toFixed(2)}`,
      };
      // The transformer strips recording / recording_player automatically.
      // We deliberately DO NOT add them here — the omission is the contract.

      callRows.push(
        transformCallRailCallRow(rawRow, { accountId, companyId: null }),
      );
      callIndex++;
    }
  }
  for (const row of callRows) records.push(row);

  // ───────────────────────────────────────────────────────────────────────
  // 4. form-submissions (30 rows) — Story 22.1 `/form_submissions.json`
  // ───────────────────────────────────────────────────────────────────────
  // Per AC #7: 73% first_form + form-name distribution + PII-safe customer +
  // PII-safe URLs + lead-status distribution + retention clamp.
  const formNameEntries: string[] = [];
  for (const f of CALLRAIL_FIXTURE_FORM_NAMES) {
    for (let i = 0; i < f.count; i++) formNameEntries.push(f.name);
  }
  const formLeadStatusEntries: string[] = [];
  for (const l of CALLRAIL_FIXTURE_FORM_LEAD_STATUSES) {
    for (let i = 0; i < l.count; i++) formLeadStatusEntries.push(l.status);
  }

  for (let f = 0; f < formSubmissionsCount; f++) {
    const isFirstForm = f < firstFormSubmissionsCount; // 22 of 30 = 73%
    // Pass 2 Edge Hunter — modulo-wrap the fixed-length distribution arrays
    // so a future baseMetrics override with `formSubmissionsCount > 30`
    // doesn't read `undefined` and crash htmlEscape on it.
    const formName = formNameEntries[f % formNameEntries.length]!;
    const leadStatus =
      formLeadStatusEntries[f % formLeadStatusEntries.length]!;
    const customerName =
      CALLRAIL_FIXTURE_FORM_CUSTOMER_NAMES[
        f % CALLRAIL_FIXTURE_FORM_CUSTOMER_NAMES.length
      ]!;
    // Form-submissions use disjoint sub-pool [50..79] within the FCC block
    // → (404) 555-0150..0179. Disjoint from calls-list (which uses 0..49)
    // and tracking numbers (80..99). For up to 30 forms each gets a unique
    // phone; >30 forms wrap to repeat-caller pattern.
    const customerPhone = buildFccPhone(50 + (f % 30));

    // Spread submissions across the 90-day window.
    const offsetDays = randomInt(rng, 1, days - 1);
    const submittedMsRaw =
      recencyCeilingMs - offsetDays * 24 * 60 * 60 * 1000;
    const submittedMs = clampToRetention(
      submittedMsRaw,
      retentionFloorMs,
      recencyCeilingMs,
    );

    const sourceForForm = CALLRAIL_FIXTURE_SOURCES[
      f % CALLRAIL_FIXTURE_SOURCES.length
    ]!;
    const rawRow: Record<string, unknown> = {
      id: `demo-form-${f + 1}`,
      submitted_at: new Date(submittedMs).toISOString(),
      form_name: htmlEscape(formName),
      form_url: `https://${target}/intake-form`,
      first_form: isFirstForm,
      landing_page_url: `https://${target}/`,
      referring_url: `https://${target}/about`,
      source: sourceForForm.slug,
      keywords: "",
      utm_source: sourceForForm.slug,
      utm_medium: sourceForForm.hasGclid ? "cpc" : "organic",
      utm_content: "",
      utm_campaign: "",
      customer_phone_number: customerPhone,
      customer_name: htmlEscape(customerName),
      customer_email: "[redacted]",
      lead_status: leadStatus,
      tags: ["form-intake"],
      value: leadStatus === "good_lead" ? randomInt(rng, 100, 500) : 0,
      milestones: [],
    };
    records.push(
      transformCallRailFormSubmissionRow(rawRow, {
        accountId,
        companyId: null,
      }),
    );
  }

  // ───────────────────────────────────────────────────────────────────────
  // 5. text-messages stub (1 row) — discoverEndpoints support, no widget binding
  // ───────────────────────────────────────────────────────────────────────
  // DEFERRED: text-messages widgets land in Story 22.x SMS expansion.
  // Minimum-shape row so `discoverEndpoints` continues to render the
  // endpoint as `available`. Pass 3 review fix — SMS stub phone reuses the
  // last tracking-pool slot (index 99 → 0199) instead of colliding with
  // form-submissions slot 50 (0150). Name pattern `Customer-SM` matches
  // the form-pool dash-prefix convention so cross-record-by-name lookups
  // don't surface mismatched record types.
  records.push({
    _reportType: "text-messages",
    accountId,
    id: "demo-sms-1",
    direction: "outbound",
    customerPhoneNumber: buildFccPhone(99),
    customerName: htmlEscape("Customer-SM"),
    messageBody: htmlEscape("[redacted SMS body — Story 22.x SMS work]"),
    sentAt: new Date(
      clampToRetention(
        recencyCeilingMs - 5 * 24 * 60 * 60 * 1000,
        retentionFloorMs,
        recencyCeilingMs,
      ),
    ).toISOString(),
    threadId: "demo-thread-1",
    lastMessageAt: new Date(
      clampToRetention(
        recencyCeilingMs - 4 * 24 * 60 * 60 * 1000,
        retentionFloorMs,
        recencyCeilingMs,
      ),
    ).toISOString(),
    firstText: true,
  });

  // ───────────────────────────────────────────────────────────────────────
  // 6. accounts-list stub (1 row) — connect-flow probe response shape
  // ───────────────────────────────────────────────────────────────────────
  // DEFERRED: no widget bindings in Story 22.2. Represents the
  // `/v3/a.json` probe response from Story 22.1 AC #2.
  records.push({
    _reportType: "accounts-list",
    id: accountId,
    name: htmlEscape("Summit Legal Group (Demo)"),
    status: "active",
    outboundNumber: "+14045550100",
    level: "owner",
  });

  // ───────────────────────────────────────────────────────────────────────
  // metadata
  // ───────────────────────────────────────────────────────────────────────
  // Per AC #2: dataFreshnessLagHours: 1 (CallRail near-real-time), planTier
  // "premium" (so Sentiment Pie demos with populated state),
  // conversationIntelligenceEntitled: true (Round 4 ADR-D — fixture-mode
  // bypass), allowedAccountIds: [accountId], companyId: null,
  // fixtureSchemaVersion. Round 1 QA Test Architect invariant: warnings = [].
  // ───────────────────────────────────────────────────────────────────────
  const fixtureGeneratedAt = new Date(fixtureGeneratedAtMs).toISOString();
  const dataFinalizedThrough = new Date(
    fixtureGeneratedAtMs - 60 * 60 * 1000, // 1 hour lag
  ).toISOString();

  return {
    platform: "callrail",
    accountId,
    dateRange: { start: config.startDate, end: config.endDate },
    records,
    fetchedAt: config.endDate,
    recordCount: records.length,
    metadata: {
      accountId,
      target,
      currencyCode: "USD",
      timeZone: "America/New_York",
      region: "US",
      dataFreshnessLagHours: 1,
      dataFinalizedThrough,
      fixtureGeneratedAt,
      fixtureSchemaVersion: CALLRAIL_FIXTURE_SCHEMA_VERSION,
      // Round 4 ADR-D — fixture-mode helper force-overwrites this to true
      // regardless, but the fixture stamps it for clarity.
      conversationIntelligenceEntitled: true,
      planTier: "premium",
      companyId: null,
      allowedAccountIds: [accountId],
    },
    warnings: [],
  };
}
